import { Token, TokenType } from '@/api/dashboard'
import { MathOperatorType } from './types'

export class MathTokenizer {
  expression: string
  private position: number
  private currentChar: string | null

  constructor(expression: string) {
    this.expression = expression.trim()
    this.position = 0
    this.currentChar = this.position < this.expression.length ? this.expression[this.position] : null
  }

  private advance(): void {
    this.position++
    this.currentChar = this.position < this.expression.length ? this.expression[this.position] : null
  }

  private peek(offset: number = 1): string | null {
    const peekPos = this.position + offset
    return peekPos < this.expression.length ? this.expression[peekPos] : null
  }

  private skipWhitespace(): void {
    while (this.currentChar && /\s/.test(this.currentChar)) {
      this.advance()
    }
  }

  private readNumber(): number {
    let result = ''
    let hasDecimal = false

    while (this.currentChar && (/\d/.test(this.currentChar) || (this.currentChar === '.' && !hasDecimal))) {
      if (this.currentChar === '.') {
        hasDecimal = true
      }
      result += this.currentChar
      this.advance()
    }

    return parseFloat(result)
  }

  private readVariable(): string {
    let result = ''

    // Variable must start with a letter
    if (!this.currentChar || !/[a-zA-Z]/.test(this.currentChar)) {
      throw new Error(`Invalid variable name at position ${this.position}`)
    }

    // Read letters and numbers
    while (this.currentChar && /[a-zA-Z0-9]/.test(this.currentChar)) {
      result += this.currentChar
      this.advance()
    }

    return result
  }

  private readOperator(): string {
    let result = ''

    // Check for two-character operators first
    if (this.currentChar && this.peek()) {
      const twoChar = this.currentChar + this.peek()
      if (Object.values(MathOperatorType).includes(twoChar as MathOperatorType)) {
        result = twoChar
        this.advance()
        this.advance()
        return result
      }
    }

    // Single character operators
    if (this.currentChar && Object.values(MathOperatorType).includes(this.currentChar as MathOperatorType)) {
      result = this.currentChar
      this.advance()
      return result
    }

    throw new Error(`Invalid operator at position ${this.position}`)
  }

  public tokenize(): Token[] {
    const tokens: Token[] = []

    while (this.currentChar) {
      this.skipWhitespace()

      if (!this.currentChar) {
        break
      }

      // Numbers
      if (/\d/.test(this.currentChar)) {
        const number = this.readNumber()
        tokens.push({
          type: TokenType.Number,
          value: number,
        })
      }
      // Variables (start with letter)
      else if (/[a-zA-Z]/.test(this.currentChar)) {
        const variable = this.readVariable()
        tokens.push({
          type: TokenType.Variable,
          value: variable,
        })
      }
      // Operators
      else if (this.isOperatorChar(this.currentChar)) {
        const operator = this.readOperator()
        tokens.push({
          type: TokenType.Operator,
          value: operator,
        })
      } else {
        throw new Error(`Invalid character '${this.currentChar}' at position ${this.position}`)
      }
    }

    return tokens
  }

  private isOperatorChar(char: string): boolean {
    // Check if character could be part of an operator
    const operatorChars = ['+', '-', '*', '/', '%', '(', ')', '^']
    return operatorChars.includes(char)
  }

  public validate(): { isValid: boolean; error?: string } {
    try {
      // Check for special characters (only allow numbers, letters, arithmetic operators, spaces, and parentheses)
      const allowedPattern = /^[a-zA-Z0-9\s+\-*/%.*^()]+$/
      if (!allowedPattern.test(this.expression)) {
        return {
          isValid: false,
          error: 'Expression contains forbidden special characters',
        }
      }

      // Try to tokenize to validate syntax
      this.tokenize()
      return { isValid: true }
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Unknown validation error',
      }
    }
  }
}
